import MainProject from '@/model/MainProject'
import {
  PROJECT_MALL_NEW_FILE_NAME,
  writeMallNewBin,
} from '@/features/project-mall/runtime'

/** 按目录名查找，优先精确匹配，其次大小写不敏感匹配。 */
export async function getDirectoryHandleCaseInsensitive(
  parent: FileSystemDirectoryHandle,
  targetName: string,
): Promise<FileSystemDirectoryHandle> {
  try {
    return await parent.getDirectoryHandle(targetName, { create: false })
  } catch {
    const lowered = targetName.toLowerCase()
    for await (const entry of parent.values()) {
      if (entry.kind === 'directory' && entry.name.toLowerCase() === lowered) {
        return entry
      }
    }
    throw new Error(`未找到目录：${targetName}`)
  }
}

export async function getMallNewBinHandle(
  dataDir: FileSystemDirectoryHandle,
  create = false,
): Promise<FileSystemFileHandle> {
  return await dataDir.getFileHandle(PROJECT_MALL_NEW_FILE_NAME, { create })
}

function sanitizeFileBaseName(name: string): string {
  const t = name.trim().replace(/[/\\?*:|"<>]/g, '_')
  return t || 'project'
}

/** 新建工程约定主数据文件名（见 docs/ue/新建.md） */
export const NEW_PROJECT_BIN_NAME = 'ProjectS.bin'
/** 新建工程约定工程标记文件名 */
export const NEW_PROJECT_MARKER_NAME = 'avgEditor.avgmakerONew'

const GRAPHICS_SUBDIRS = [
  'Background',
  'Block',
  'Button',
  'Chat',
  'Face',
  'Half',
  'Oafs',
  'Mood',
  'Other',
  'System',
  'UI',
] as const

const AUDIO_SUBDIRS = ['BGM', 'BGS', 'SE', 'Voice'] as const

/**
 * 在工程根目录 B 下创建 Data、Graphics（含子目录）、Font、audio（含子目录）等空目录结构。
 * 与 docs/ue/新建.md 一致。
 */
export async function ensureNewProjectDirectoryLayout(
  projectRoot: FileSystemDirectoryHandle,
): Promise<void> {
  await projectRoot.getDirectoryHandle('Data', { create: true })
  const graphics = await projectRoot.getDirectoryHandle('Graphics', { create: true })
  for (const n of GRAPHICS_SUBDIRS) {
    await graphics.getDirectoryHandle(n, { create: true })
  }
  await projectRoot.getDirectoryHandle('Font', { create: true })
  const audio = await projectRoot.getDirectoryHandle('audio', { create: true })
  for (const n of AUDIO_SUBDIRS) {
    await audio.getDirectoryHandle(n, { create: true })
  }
}

/**
 * 在父文件夹 A 下创建以工程名命名的子文件夹 B（名称经安全化）。
 */
export async function createProjectRootUnderParent(
  parentHandle: FileSystemDirectoryHandle,
  projectDisplayName: string,
): Promise<FileSystemDirectoryHandle> {
  const dirName = sanitizeFileBaseName(projectDisplayName)
  return parentHandle.getDirectoryHandle(dirName, { create: true })
}

/**
 * 合并/发布时写入主 bin：优先覆盖已有 ProjectS.bin 或 projects.bin。
 */
export async function getWritableDataBinHandle(
  dataDir: FileSystemDirectoryHandle,
): Promise<FileSystemFileHandle> {
  const tryNames = [NEW_PROJECT_BIN_NAME, 'projects.bin', 'Projects.bin', 'projectS.bin']
  for (const name of tryNames) {
    try {
      return await dataDir.getFileHandle(name)
    } catch {
      /* try next */
    }
  }
  return dataDir.getFileHandle('projects.bin', { create: true })
}

/**
 * 在工程根目录 B 下写入 Data/ProjectS.bin、avgEditor.avgmakerONew，并保证目录结构已创建。
 */
export async function writeNewProjectToRoot(
  projectRootHandle: FileSystemDirectoryHandle,
  mainProject: MainProject,
): Promise<{ markerFileName: string; binFileName: string }> {
  await ensureNewProjectDirectoryLayout(projectRootHandle)
  const dataDir = await projectRootHandle.getDirectoryHandle('Data', { create: true })
  const binHandle = await dataDir.getFileHandle(NEW_PROJECT_BIN_NAME, { create: true })

  const bytes = mainProject.writeBinBuffer()

  const writable = await binHandle.createWritable()
  const copy = new Uint8Array(bytes.byteLength)
  copy.set(bytes)
  await writable.write(copy)
  await writable.close()

  const mallNewHandle = await getMallNewBinHandle(dataDir, true)
  const mallNewWritable = await mallNewHandle.createWritable()
  const mallNewBytes = writeMallNewBin(mainProject)
  const mallNewCopy = new Uint8Array(mallNewBytes.byteLength)
  mallNewCopy.set(mallNewBytes)
  await mallNewWritable.write(mallNewCopy)
  await mallNewWritable.close()

  const markerFileName = NEW_PROJECT_MARKER_NAME
  const markerHandle = await projectRootHandle.getFileHandle(markerFileName, { create: true })
  const markerWritable = await markerHandle.createWritable()
  const meta = JSON.stringify(
    { version: 1, name: mainProject.storyTree.ProjectName, createdAt: Date.now() },
    null,
    0,
  )
  await markerWritable.write(new TextEncoder().encode(meta))
  await markerWritable.close()

  return { markerFileName, binFileName: NEW_PROJECT_BIN_NAME }
}

